import { useCallback, useEffect, useRef } from 'react';

import type { CropSeasonOption } from './types';

interface Props {
  enabled: boolean;
  selectedRegion?: string;
  selectedSeason?: string;
  locationKey: string;
  locationPathname: string;
  stateRegion?: string;
  isLoading: boolean;
  options: CropSeasonOption[];
  automaticSelectionAttempts?: Set<string>;
  onSelectSeason: (region: string, season: string) => void;
}

interface Result {
  candidate?: CropSeasonOption;
  armForRegion: (region: string) => void;
}

const useAutomaticCropSeasonSelection = ({
  enabled,
  selectedRegion,
  selectedSeason,
  locationKey,
  locationPathname,
  stateRegion,
  isLoading,
  options,
  automaticSelectionAttempts,
  onSelectSeason
}: Props): Result => {
  const localSelectionAttempts = useRef(new Set<string>()).current;
  const selectionAttempts =
    automaticSelectionAttempts ?? localSelectionAttempts;
  const attemptKey = selectedRegion
    ? JSON.stringify([selectedRegion, locationKey])
    : undefined;
  const attemptConsumed = Boolean(
    attemptKey && selectionAttempts.has(attemptKey)
  );
  const automaticSelectionRegion = useRef<string | undefined>(
    selectedSeason || attemptConsumed ? undefined : selectedRegion
  );
  const armForRegion = useCallback((region: string) => {
    automaticSelectionRegion.current = region;
  }, []);

  const isAutomaticSelectionRegionCurrent =
    automaticSelectionRegion.current === stateRegion ||
    (!selectedSeason && selectedRegion === stateRegion);
  const candidate =
    enabled &&
    !attemptConsumed &&
    Boolean(attemptKey) &&
    !isLoading &&
    isAutomaticSelectionRegionCurrent
      ? (options.find(option => option.isActive) ?? options[0])
      : undefined;

  useEffect(() => {
    if (!enabled || !selectedRegion) {
      return;
    }

    if (!attemptKey || selectedSeason || selectionAttempts.has(attemptKey)) {
      automaticSelectionRegion.current = undefined;
      return;
    }

    automaticSelectionRegion.current = selectedRegion;
  }, [
    enabled,
    attemptKey,
    locationPathname,
    locationKey,
    selectionAttempts,
    selectedSeason,
    selectedRegion
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (
      selectedSeason ||
      !selectedRegion ||
      !attemptKey ||
      stateRegion !== selectedRegion ||
      isLoading ||
      automaticSelectionRegion.current !== selectedRegion ||
      selectionAttempts.has(attemptKey)
    ) {
      return;
    }

    const firstSeason = options.find(option => option.isActive) ?? options[0];
    selectionAttempts.add(attemptKey);
    automaticSelectionRegion.current = undefined;

    if (firstSeason) {
      onSelectSeason(selectedRegion, firstSeason.value);
    }
  }, [
    enabled,
    selectedSeason,
    selectedRegion,
    attemptKey,
    stateRegion,
    isLoading,
    automaticSelectionRegion,
    selectionAttempts,
    options,
    onSelectSeason,
    locationPathname
  ]);

  return { candidate, armForRegion };
};

export default useAutomaticCropSeasonSelection;
